import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { getAdditionalInformation } from '../../../api/Optimise';
import { AppContext } from '../../../context/AppContext';
import { useInverterConnectionStatus } from '../../../hooks';
import type { AdditionalOnboardingInformation } from '../../../types/optimise';
import ensureError from '../../../utils/ensureError';
import ErrorLabel from '../../ErrorLabel';
import InverterConnectionModal from '../../Modal/InverterConnection/InverterConnectionModal';
import { StyledHr, StyledSemiboldLabel } from '../../Styled';

import InformationPanel from './InformationPanel';

interface AppContextValues {
  userSub: string | null;
}

const StatusBadge = styled.span<{ $connected: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  color: ${(props) => props.theme.colors.white};
  font-weight: 700;
  font-size: 14px;
  background: ${(props) => (props.$connected ? props.theme.colors.success : props.theme.colors.grey)};
`;

const InverterConnectionSettings = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { userSub } = useContext(AppContext) as unknown as AppContextValues;
  const { isChecking, isConnected } = useInverterConnectionStatus();
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [additionalInformation, setAdditionalInformation] = useState<AdditionalOnboardingInformation | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userSub) return;
    getAdditionalInformation(userSub)
      .then((res) => {
        setAdditionalInformation(res);
        setLoadError(null);
      })
      .catch((err: unknown) => setLoadError(ensureError(err)));
  }, [userSub]);

  const inverter = additionalInformation?.hardware_details?.inverter;
  const statusLabel = isChecking ? 'Checking...' : isConnected ? 'Connected' : 'Disconnected';

  return (
    <>
      <InverterConnectionModal
        show={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        onConnected={() => {
          setShowConnectionModal(false);
          void navigate('/app');
        }}
        initialManufacturer={inverter?.manufacturer}
      />
      <InformationPanel title="Inverter details">
        {loadError && (
          <li>
            <ErrorLabel label="Something went wrong loading your inverter details. Please try again later." />
          </li>
        )}
        <li>
          <StyledSemiboldLabel>Inverter manufacturer:</StyledSemiboldLabel> {inverter?.manufacturer ?? '---'}
        </li>
        <li>
          <StyledSemiboldLabel>Inverter model:</StyledSemiboldLabel> {inverter?.model ?? '---'}
        </li>
        <li>
          <StyledSemiboldLabel>Inverter status:</StyledSemiboldLabel>{' '}
          <StatusBadge $connected={!isChecking && isConnected}>{statusLabel}</StatusBadge>
        </li>
        <StyledHr />
        <li>
          If you've recently changed your inverter's password, or your inverter's API token has expired, then you'll
          need to update your connection details to ensure {theme.language.appName} continues working correctly.
        </li>
        <li>
          <Button variant="link" className="p-0" onClick={() => setShowConnectionModal(true)}>
            Reset my connection details
          </Button>
        </li>
      </InformationPanel>
    </>
  );
};

export default InverterConnectionSettings;
